﻿import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import 'components/Tabs/tabs.scss';

export interface Tab<T> {
    Data: T;
    HasChanges?: boolean;
    Id: number;
    JustCreated?: boolean;
    Page?: 'Students' | 'Transactions' | 'Groups' | 'Users' | 'Partners';
    Title: string;
}

interface CreateNewAction<T> {
    tab: Tab<T>;
    type: 'CreateNew';
}

interface FocusMultiple<T> {
    tabs: Tab<T>[];
    type: 'FocusMultiple';
}

interface CreateAction<T> {
    tab: Tab<T>;
    type: 'CreateOrFocus';
}

interface UpdateDataAction<T> {
    dataToMergeIn: Partial<T>;
    tabId: number;
    type: 'UpdateData';
}

interface UpdateTitleAction {
    hasChanges: boolean;
    newTitle: string;
    tabId: number;
    type: 'UpdateTitle';
}

interface DeleteAction {
    tabId: number;
    type: 'Delete';
    urlParamId?: string;
}

type DispatchAction<T> =
    | CreateNewAction<T>
    | CreateAction<T>
    | UpdateDataAction<T>
    | UpdateTitleAction
    | DeleteAction
    | FocusMultiple<T>;

export interface TabsContext<T> {
    openTabIndex: number;
    pageContext: Tab<T> | null;
    setOpenTabIndex: (value: number) => void;
    setSubTabIndex: Dispatch<SetStateAction<number[]>>;
    setTabs: Dispatch<SetStateAction<Tab<T>[]>>;
    subTabIndex: number[];
    tabs: Tab<T>[];
    tabsDispatch: Dispatch<DispatchAction<T>>;
}

interface TabsContextProviderProps<T> {
    children: React.ReactNode;
    context: React.Context<TabsContext<T>>;
    disableUrlSync?: boolean;
}

const setInitialTabIndexes = () => {

    const searchParams = new URLSearchParams(window.location.search);
    const subTabIndexes = [];

    for (const [key, value] of searchParams.entries()) {

        if (/^tab_\d+$/.test(key)) {

            const parsed = Number(value);
            if (!isNaN(parsed)) {

                subTabIndexes.push(parsed);

            }

        }

    }

    return subTabIndexes;

};

const setInitialParentTabIndex = () => {

    const searchParams = new URLSearchParams(window.location.search);
    const activeTab = searchParams.get('active-tab');

    if (activeTab !== null) {

        const index = Number(activeTab);
        return isNaN(index) ? 1 : index;

    }

    return 1;

};

export function TabsContextProvider<T>({ children, context: { Provider }, disableUrlSync = false }: TabsContextProviderProps<T>) {

    const [tabs, setTabs] = useState<Tab<T>[]>([]);
    const [openTabIndex, setOpenTabIndex] = useState(0);
    const [pageContext, setPageContext] = useState<Tab<T> | null>(null);
    const [subTabIndex, setSubTabIndex] = useState<number[]>(setInitialTabIndexes());

    useEffect(() => {

        if (tabs.length > 0) {

            setPageContext(tabs[openTabIndex - 1]);
            if (tabs.length > subTabIndex.length) {

                setSubTabIndex([...subTabIndex, 0]);

            }

        }
        if (tabs.length === 0) {

            setPageContext(tabs[openTabIndex]);

        }

    }, [openTabIndex, tabs]);

    useEffect(() => {

        if (!disableUrlSync && openTabIndex !== 0 && subTabIndex.length > 0) {

            const url = new URL(window.location.href);
            const search_params = url.searchParams;
            search_params.set(`active-tab`, openTabIndex.toString());
            search_params.set(`tab_${openTabIndex}`, subTabIndex[openTabIndex - 1] ? subTabIndex[openTabIndex - 1].toString() : '0');
            url.search = search_params.toString();
            window.history.replaceState(null, '', url);

        }

    }, [subTabIndex, openTabIndex]);

    const deleteTab = (tabId: number, urlParamString: string | undefined) => {

        const newTabs = tabs.filter(item => item.Id !== tabId);

        if (openTabIndex !== 0) {

            const openTab = tabs[openTabIndex - 1];
            if (openTab.Id === tabId) {

                setOpenTabIndex(openTabIndex - 1);

            } else {

                const newOpenTabIndex = newTabs.findIndex(newTab => newTab.Id === openTab.Id);
                setOpenTabIndex(newOpenTabIndex + 1);

            }

        }

        const copySubTabIndex = [...subTabIndex];
        copySubTabIndex.splice(tabs.findIndex(oldTab => oldTab.Id !== tabId) - 1, 1);
        setSubTabIndex(copySubTabIndex);
        setTabs(prevState => prevState.filter(oldTab => oldTab.Id !== tabId));

        const url = new URL(window.location.href);
        const searchParams = url.searchParams;

        // This handles everything with remove the tab_{index}={value} params.
        const entriesToUpdate: [number, string][] = [];

        for (const key of Array.from(searchParams.keys())) {

            if (key.startsWith('tab_')) {

                const match = key.match(/^tab_(\d+)$/);
                if (match) {

                    const index = Number(match[1]);

                    if (newTabs.length === 0) {

                        searchParams.delete(key);
                        searchParams.delete('active-tab');

                    } else {

                        const tabAtIndex = tabs[index - 1];

                        if (tabAtIndex?.Id === tabId) {

                            searchParams.delete(key);

                        } else if (tabAtIndex) {

                            const value = searchParams.get(key);
                            if (value !== null && index > 1) {

                                entriesToUpdate.push([index, value]);

                            }
                            searchParams.delete(key);

                        }

                    }

                }

            }

        }

        // Re-add shifted tab params (only if some remain)
        if (newTabs.length > 0) {

            for (const [oldIndex, value] of entriesToUpdate) {

                const newIndex = oldIndex - 1;
                searchParams.set(`tab_${newIndex}`, value);

            }

        }

        // Clean up the comma-separated list param (urlParamString)
        const urlIdListString = searchParams.get(urlParamString ?? '');
        const newString: number[] = [];

        if (urlIdListString) {

            urlIdListString.split(',').forEach(number => {

                const parsed = parseInt(number);
                if (parsed !== tabId) {

                    newString.push(parsed);

                }

            });

        }

        // Remove or update urlParamString param
        if (urlParamString) {

            if (newString.length > 0) {

                searchParams.set(urlParamString, newString.toString());

            } else {

                searchParams.delete(urlParamString);

            }

        }

        // Update the URL in browser
        url.search = searchParams.toString();
        window.history.pushState(null, '', url);

    };

    const createOrFocusTab = (tab: Tab<T>, createNew: boolean) => {

        let tabsToMap = [...tabs];
        if (createNew) {

            tabsToMap = tabsToMap.filter(tab => tab.Id !== 0);

        }
        for (let i = 0; i < tabsToMap.length; i++) {

            const currentTab = tabsToMap[i];
            if (currentTab.Id === tab.Id) {

                tabsToMap[i] = { ...tab };
                setOpenTabIndex(i + 1);
                setTabs(tabsToMap);
                return;

            }

        }
        setTabs([...tabsToMap, tab]);
        setOpenTabIndex(tabsToMap.length + 1);
        const element = document.getElementById(tabs.length.toString());
        element?.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
        });

    };

    const focusMultipleTabs = (tabs: Tab<T>[]) => {

        setTabs(tabs);
        setOpenTabIndex(setInitialParentTabIndex());
        setSubTabIndex([...subTabIndex, 0]);

    };

    const updateTabData = (tabId: number, dataToMergeIn: Partial<T>) => {

        setTabs(prevTabs => prevTabs.map(tab => (tab.Id === tabId ? { ...tab, Data: { ...tab.Data, ...dataToMergeIn } } : tab)));

    };

    const updateTabTitle = (tabId: number, newTitle: string, hasChanges: boolean) => {

        setTabs(tabs.map(tab => (tab.Id === tabId ? { ...tab, HasChanges: hasChanges, Title: newTitle } : tab)));

    };

    const tabsDispatch = (action: DispatchAction<T>) => {

        switch (action.type) {

            case 'CreateNew':
                createOrFocusTab(action.tab, true);
                break;
            case 'CreateOrFocus':
                createOrFocusTab(action.tab, false);
                break;
            case 'FocusMultiple':
                focusMultipleTabs(action.tabs);
                break;
            case 'UpdateData':
                return updateTabData(action.tabId, action.dataToMergeIn);
            case 'UpdateTitle':
                return updateTabTitle(action.tabId, action.newTitle, action.hasChanges);
            case 'Delete':
                return deleteTab(action.tabId, action.urlParamId ? action.urlParamId : undefined);

        }

    };

    return (
        <Provider
            value={{
                openTabIndex,
                pageContext,
                setOpenTabIndex,
                setSubTabIndex,
                setTabs,
                subTabIndex,
                tabs,
                tabsDispatch
            }}
        >
            {children}
        </Provider>
    );

}
